<script setup>
	import { reactive, watch } from "vue";
	import { VDialog, VCard, VToolbar, VToolbarTitle, VSpacer, VCardText, VRow, VCol, VTextField, VSelect, VTextarea, VInput, VCheckbox } from "vuetify/components";
	import { academicService, executeServiceCall, getValidationErrorsFromResponse } from "@/services";
	import { hasAnySimpleValueChanged, hasArrayChanged } from "@/utils";
	import { CloseButton, ConfirmDialog, DeleteButton, ErrorAlert, LoadingIndicator, ReactivateButton, SaveAndCloseButton, SaveAndNewButton, SnackbarMessage, WarningMessagesAlert } from "@/components";
	import { StudentIcon } from ".";

	// Public Interface
	const props = defineProps({
		studentId: { type: String, required: false },
		shouldBeEditing: { type: Boolean, required: true },
	});
	const emit = defineEmits([ "closed", "studentUpdated" ]);
	function emitClosed() { emit("closed"); }
	function emitStudentUpdated() { emit("studentUpdated", mainData.record.id); }

	// Data
	const mainData = reactive({
		hasCriticalError: false,
		ignoreIsDirty: false,
		isBusy: false,
		isDirty: false,
		isShowing: false,
		originalRecord: null,
		record: null,
		validationErrors: [],

		// Methods
		close(force) {
			if (!!force)
				this.ignoreIsDirty = true;
			this.isShowing = false;
		},
		resetErrors() {
			this.hasCriticalError = false;
			this.validationErrors = [];
		},
		setCriticalError(error) {
			console.error(error);
			this.hasCriticalError = true;
		},
		setIsBusy(isBusy) {
			this.isProcessing = isBusy;
		},
		setRecord(record) {
			this.record = record;
			this.originalRecord = JSON.parse(JSON.stringify(record));

			this.ignoreIsDirty = record === null;
			this.isDirty = false;
		},
		setValidationErrors(response) {
			this.validationErrors = getValidationErrorsFromResponse(response);
		},
	});
	const promptDialog = reactive({
		isPromptingUser: false,
		promptConfirmationAction: null,
		userPrompt: "",

		// Methods
		/**
		 * Prompts the user with a confirmation dialog.
		 * Returns a Promise that resolves to true if the user confirms, or false if they cancel.
		 * @param {string} userPrompt - The message to display in the confirmation dialog.
		 * @return {Promise<boolean>} - Promise resolving to user's choice.
		 */
		confirm(userPrompt) {
			return new Promise(resolve => {
				this.promptConfirmationAction = (isConfirmed) => {
					this.isPromptingUser = false;
					this.promptConfirmationAction = null;
					resolve(isConfirmed);
				};
				this.userPrompt = userPrompt;
				this.isPromptingUser = true;
			});
		},
		onCancel() {
			this.isPromptingUser = false;
			if (this.promptConfirmationAction)
				this.promptConfirmationAction(false);
		},
		onConfirm() {
			this.isPromptingUser = false;
			if (this.promptConfirmationAction)
				this.promptConfirmationAction(true);
		},
	});
	const snackbar = reactive({
		isShowing: false,
		message: "",
		
		// Methods
		showMessage(message) {
			this.message = message;
			this.isShowing = true;
		},
	});

	// Functions
	async function deleteStudent() {
		if (!props.studentId)
			return;

		mainData.resetErrors();
		mainData.setIsBusy(true);

		await executeServiceCall(() => academicService.student.delete(props.studentId))
			.then(() => {
				snackbar.showMessage("The Student was deleted successfully.");
				emitStudentUpdated();
				mainData.setStudentData(null);
				mainData.close();
			})
			.catch(error => mainData.setCriticalError(error))
			.finally(() => mainData.setIsBusy(false));
	};
	async function loadStudent() {
		mainData.resetErrors();
		mainData.setIsBusy(true);

		if (!props.studentId) {
			mainData.setStudentData({
				id: "",
				name: "",
				address: "",
				personalEmail: "",
				lucentaryEmail: "",
				enrollmentDate: "",
				courseName: props.courseName,
				apprenticeOrder: props.apprenticeOrder,
				description: "",
				objectives: [],
				materialFiles: [],
				materialCount: 0,
				isDeleted: false,
			});
			mainData.setIsBusy(false);
		}
		else {
			await executeServiceCall(() => academicService.student.byId(props.studentId))
				.then(({ data }) => mainData.setRecord(data))
				.catch(error => mainData.setCriticalError(error))
				.finally(() => mainData.setIsBusy(false));
		}
	};
	async function reactivateStudent() {
		if (!props.studentId)
			return;

		mainData.resetErrors();
		mainData.setIsBusy(true);

		await executeServiceCall(() => academicService.class.reactivate(props.studentId))
			.then(() => {
				snackbar.showMessage("The Student was reactivated successfully.");
				emitStudentUpdated();
				mainData.setRecord(null);
				mainData.close();
			})
			.catch(error => mainData.setCriticalError(error))
			.finally(() => mainData.setIsBusy(false));
	};
	async function saveStudent() {
		mainData.resetErrors();
		mainData.setIsBusy(true);

		await executeServiceCall(() => academicService.student.save(mainData.record))
			.then(({ data }) => {
				mainData.record.id = String(data);
				snackbar.showMessage("The Student was saved successfully.");
				emitStudentUpdated();
			})
			.catch((error) => {
				if (error.response?.status === 400)
					mainData.setValidationErrors(error.response);
				else
					mainData.setCriticalError(error);
			})
			.finally(() => mainData.setIsBusy(false));
	}
	async function saveStudentAndNew() {
		await saveStudent()
			.then(async () => {
				props.studentId = null;
				await loadStudent();
			});
	}
	async function saveStudentAndClose() {
		await saveStudent()
			.then(() => {
				mainData.close(true);
			});
	}
	function updateIsDirty() {
		if (!mainData.record || !mainData.originalRecord)
			return;

		const isDirty = hasAnySimpleValueChanged(
			mainData.record,
			mainData.originalRecord,
			["name","courseName","description"])
			|| hasArrayChanged(mainData.record.objectives, mainData.originalRecord.objectives);
		mainData.isDirty = isDirty;
	}

	watch(() => mainData.record, () => { updateIsDirty(); }, { deep: true });
	watch(() => props.shouldBeEditing, async () => {
		if (props.shouldBeEditing) {
			mainData.isShowing = true;
			await loadStudent();
		}
	});
	watch(() => mainData.isShowing, async () => {
		if (!mainData.isShowing && props.isDirty && !mainData.ignoreIsDirty) {
			if (await promptDialog.confirm("You have unsaved changes.  Are you sure you want to close and lose your unsaved changes?")) {
				mainData.ignoreIsDirty = true;
			}
			else {
				mainData.isShowing = true;
			}
		}

		if (!mainData.isShowing)
			emitClosed();
	});
</script>

<template>
	<div>
		<VDialog v-model="mainData.isShowing"
			class="base-dialog"
			:fullscreen="true"
			scrim="true"
			transition="dialog-bottom-transition">
			<VCard>
				<VToolbar color="primary">
					<VToolbarTitle><StudentIcon/> Student Management</VToolbarTitle>
					<VSpacer />
					<ReactivateButton v-if="props.studentId" :disabled="mainData.isBusy" @click="reactivateStudent" />
					<DeleteButton v-if="props.studentId" :disabled="mainData.isBusy" @click="deleteStudent" />
					<SaveAndNewButton :disabled="!mainData.isDirty || mainData.isBusy" @click="saveStudentAndNew" />
					<SaveAndCloseButton :disabled="!mainData.isDirty || mainData.isBusy" @click="saveStudentAndClose" />
					<CloseButton :disabled="mainData.isBusy" @click="emitClosed" />
				</VToolbar>
				<VCardText>
					<ErrorAlert v-if="mainData.hasCriticalError">Something went wrong.  Try again later.</ErrorAlert>
					<LoadingIndicator v-if="mainData.isBusy" />
					<div v-if="mainData.hasData">
						<WarningMessagesAlert v-if="mainData.validationErrors?.length > 0" :messages="mainData.validationErrors" />
						<div v-if="mainData.student">
							<VRow>
								<VCol class="v-col-12 v-col-md-8">
									<VTextField id="studentName"
										v-model="mainData.student.name"
										label="Name"
										hint="The name must be unique within the Course."
										:counter="50"
										:disabled="mainData.isProcessing"
										required
										autofocus />
								</VCol>
								<VCol class="v-col-12 v-col-md-4">
								<VSelect id="address"
									v-model="mainData.student.name"
									label="Address"
									:items="props.courses.map(course => course.name)"
									:disabled="mainData.isProcessing" />
								</VCol>
							</VRow>
							<VRow>
								<VCol class="v-col-12 v-col-md-6">
									<VTextarea id="classDescription"
										v-model="mainData.student.description"
										label="Description"
										hint="This description will be visible to Students."
										rows="8"
										:disabled="mainData.isProcessing" />
								</VCol>
								<VCol class="v-col-12 v-col-md-6">
									<VInput>Objectives</VInput>
									<VCheckbox v-model="mainData.lucentaryClass.objectives"
										v-for="(objective, i) in getObjectives()" :key="i"
										class="objective"
										:label="objective"
										:value="objective"
										density="compact"
										:disabled="mainData.isProcessing" />
								</VCol>
							</VRow>
						</div>
					</div>
				</VCardText>
			</VCard>
		</VDialog>

		<SnackbarMessage v-if="snackbar.isShowing" :message="snackbar.message" @close="snackbar.isShowing = false" />

		<ConfirmDialog v-if="promptDialog.isPromptingUser"
			:userPrompt="promptDialog.userPrompt"
			@confirm="promptDialog.onConfirm"
			@cancel="promptDialog.onCancel" />
	</div>
</template>
